<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useFormContext } from "../composables/useFormContext"

const props = withDefaults(defineProps<{
  /** When true, the toast never shows and Ctrl/Cmd+S is ignored, regardless of dirty state. */
  disabled?: boolean
}>(), {
  disabled: false
})

const api = useFormContext()
const { t } = useI18n()

const dotClass = computed(() => api.isValid.value ? "bg-warning" : "bg-error")

const countText = computed(() => t("forms.toast.changedCount", api.changedCount.value))
const saveLabel = computed(() => api.isSaving.value ? t("forms.toast.saving") : t("forms.toast.save"))
const discardLabel = computed(() => t("forms.toast.discard"))

// Every save implementation reports its own failure (toast) and rethrows for callers
// that must know (e.g. publish-after-save). Nobody here needs the rejection — left
// unhandled, each failed save reached GlitchTip past the noise filter (BACKEND-51).
function save() { api.save().catch(() => {}) }

function onSave() { save() }
function onDiscard() {
  if (window.confirm(t("forms.toast.discardConfirmDescription"))) {
    api.reset()
  }
}

function onKeydown(e: KeyboardEvent) {
  const isSaveShortcut = (e.metaKey || e.ctrlKey) && e.key === "s"
  if (isSaveShortcut) {
    e.preventDefault()
    if (props.disabled) return
    if (api.isDirty.value && !api.isSaving.value) {
      save()
    }
  }
}

onMounted(() => document.addEventListener("keydown", onKeydown))
onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown))
</script>

<template>
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="!disabled && api.isDirty.value"
      data-testid="dirty-toast"
      class="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2 bg-neutral text-neutral-content rounded-full shadow-xl"
      role="status"
      aria-live="polite"
    >
      <span class="w-2.5 h-2.5 rounded-full" :class="dotClass" />
      <span class="text-sm">{{ countText }}</span>
      <button
        type="button"
        data-testid="discard-button"
        class="btn btn-ghost btn-sm rounded-full"
        :disabled="api.isSaving.value"
        @click="onDiscard"
      >
        {{ discardLabel }}
      </button>
      <button
        type="button"
        data-testid="save-button"
        class="btn btn-primary btn-sm rounded-full"
        :disabled="api.isSaving.value"
        @click="onSave"
      >
        <span v-if="api.isSaving.value" class="loading loading-spinner loading-xs" />
        {{ saveLabel }}
      </button>
    </div>
  </Transition>
</template>
